import { CART_GLOBAL_VARIABLE_NAME } from '../../../app/cart/constants.js';
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Fetches pricing for the cart's contents.
 *
 * Lifted out of `Cart.js`, which had the endpoint shapes, request bodies, and
 * response unwrapping inline in a render component. Keeping it here means the
 * component no longer has to know that there are two pricing endpoints at all.
 *
 * @param {object} args
 * @param {string} args.sourceUrl checkoutdwa origin to call
 * @param {Array<{productCode: string, quantity: number}>} args.lineItems cart contents, already filtered to non-zero quantities
 * @param {string} args.currency e.g. 'usd'
 * @returns {{pricing: object|null, isLoading: boolean, error: Error|null}}
 *   `pricing` is `null` before the first response and after a failure, `{}` when
 *   the cart is empty. Components distinguish these — `null` renders a loading
 *   message — so the tri-state is deliberate rather than sloppy.
 */
const useCartPricing = ({
    sourceUrl,
    lineItems,
    currency,
}) => {
    const [pricing, setPricing] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // The cart state hook rebuilds `products` from the cookie on every window
    // focus and every bfcache restore, so `lineItems` gets a fresh array identity
    // even when nothing about the cart changed. Keying the effect on the contents
    // instead of the reference is what stops a refetch on every tab focus.
    const lineItemsKey = useMemo(
        () => lineItems.map((item) => `${item.productCode}:${item.quantity}`).join(','),
        [lineItems],
    );

    // Read through a ref inside the effect so the array itself doesn't have to be
    // a dependency — it is the reference churn described above that we're avoiding,
    // and `lineItemsKey` already captures every change that matters.
    const lineItemsRef = useRef(lineItems);
    lineItemsRef.current = lineItems;

    useEffect(() => {
        const controller = new AbortController();
        const { signal } = controller;
        const currentLineItems = lineItemsRef.current;

        if (currentLineItems.length === 0) {
            setPricing({});
            setError(null);
            setIsLoading(false);
            return undefined;
        }

        (async () => {
            setIsLoading(true);
            try {
                const response = await fetch(`${sourceUrl}/api/products/price-preview`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        lineItems: currentLineItems.map((item) => ({
                            productCode: item.productCode,
                            quantity: item.quantity,
                        })),
                        currency,
                    }),
                    signal,
                });

                const jsonResponse = await response.json();
                if (signal.aborted) {
                    return;
                }
                setPricing(jsonResponse?.pricing || {});
                setError(null);
            } catch (err) {
                if (signal.aborted || err.name === 'AbortError') {
                    return;
                }
                console.error(`[${CART_GLOBAL_VARIABLE_NAME}] Pricing fetch failed:`, err);
                setPricing(null);
                setError(err);
            } finally {
                // Guarded because an aborted request has already been superseded by
                // a newer one that set `isLoading` to true; clearing it here would
                // flash the cart out of its loading state mid-flight.
                if (!signal.aborted) {
                    setIsLoading(false);
                }
            }
        })();

        // The previous implementation checked for `AbortError` but never created a
        // controller, so nothing was ever aborted and a slow response to an old
        // quantity could land after a newer one and win.
        return () => controller.abort();
    }, [lineItemsKey, currency, sourceUrl]);

    return { pricing, isLoading, error };
};

export default useCartPricing;
